import { useCallback, useEffect, useState } from 'react';
import { supabase, MEDIA_TABLE } from '@/lib/supabase';
import type { MediaRecord } from '@/lib/types';
import { formatDateTime } from '@/lib/format';

interface MediaPickerProps {
  onSelect: (url: string) => void;
  onClose: () => void;
}

export default function MediaPicker({ onSelect, onClose }: MediaPickerProps) {
  const [items, setItems] = useState<MediaRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMedia = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from(MEDIA_TABLE)
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setItems((data as MediaRecord[]) || []);
    } catch {
      setError('メディアの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-[#1a1a2e] border border-[#2a2a3e] rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-extrabold text-white">ライブラリから選択</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-[#a0a0b0] hover:text-white hover:bg-[#2a2a3e] transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {loading ? (
          <div className="bg-[#12121f] border border-[#2a2a3e] rounded-2xl p-16 text-center text-[#a0a0b0]">
            <i className="ri-loader-4-line animate-spin text-2xl"></i>
          </div>
        ) : error ? (
          <div className="bg-[#12121f] border border-[#2a2a3e] rounded-2xl p-12 text-center">
            <p className="text-[#e8e8f0]">{error}</p>
            <button
              type="button"
              onClick={fetchMedia}
              className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-xl h-10 px-5 text-sm font-medium bg-[#1a1a2e] border border-[#2a2a3e] text-[#e8e8f0] hover:bg-[#2a2a3e] transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              再読み込み
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-[#12121f] border border-[#2a2a3e] rounded-2xl p-16 text-center">
            <i className="ri-image-2-line text-4xl text-[#7a7a8a]"></i>
            <p className="mt-4 text-[#a0a0b0]">
              ライブラリに画像がありません。先に「メディアライブラリ」タブから画像をアップロードしてください。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.url)}
                className="text-left w-full bg-[#12121f] border border-[#2a2a3e] rounded-2xl overflow-hidden hover:border-[#7c3aed] transition-colors cursor-pointer"
              >
                <div className="w-full h-36 bg-[#0d0d1a] flex items-center justify-center overflow-hidden">
                  <img
                    src={item.url}
                    alt={item.file_name || 'media'}
                    className="w-full h-36 object-cover object-top"
                  />
                </div>
                <div className="p-3">
                  <p className="text-sm text-white truncate" title={item.file_name || ''}>
                    {item.file_name || '名称未設定'}
                  </p>
                  <p className="mt-1 text-xs text-[#a0a0b0]">{formatDateTime(item.created_at) || '-'}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}